import type {
  Permission,
  RemainingEffortReport,
  RemainingEffortReportInput,
  RestaufwandProjekt,
} from '@asas/domain';
import { addWeeks, compareIsoWeeks, FACHLICHE_ZEITZONE } from '@asas/domain/iso-week';
import type { IsoWeek } from '@asas/domain/iso-week';
import {
  fruehesteBezugswoche,
  gueltigeMeldung,
  istDrift,
  istErhoehung,
  REST_STUNDEN_PATTERN,
  RESTAUFWAND_KOMMENTAR_MAX_LENGTH,
  verlangtRueckfrage,
} from '@asas/domain/restaufwand';
import type { RestaufwandAbweisung } from '@asas/domain/restaufwand';

/**
 * Restaufwand melden als reine Logik (AC-601) — ohne React, ohne Netz.
 *
 * Ob eine Meldung gilt, ob sie einen Kommentar braucht, ob sie zurueckgefragt
 * wird und ob sie Drift ist, entscheidet `@asas/domain/restaufwand` — DIESELBE
 * Stelle, mit der der Server prueft. Die Oberflaeche zeigt die Hinweise nur
 * vorab; massgeblich ist die Serverantwort.
 */

/** Das Recht hinter allen vier Prozeduren `restaufwand.*` (E8). */
export const RESTAUFWAND_RECHT: Permission = 'remaining_effort:report';

export function darfRestaufwandMelden(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(RESTAUFWAND_RECHT);
}

/** Was der Server als Grund nennt, fuer Menschen (E3, E5, E6, E9). */
export const ABWEISUNG_TEXT: Readonly<Record<RestaufwandAbweisung, string>> = {
  projekt_nicht_beauftragt: 'Das Projekt ist nicht beauftragt; Meldungen sind nur lesbar.',
  bezugswoche_in_zukunft: 'Die Bezugswoche liegt in der Zukunft.',
  bezugswoche_zu_alt: 'Die Bezugswoche liegt mehr als 26 Wochen zurück.',
  kommentar_fehlt:
    'Der Restaufwand ist höher als zuvor. Bitte begründen Sie die Erhöhung im Kommentar.',
  rueckfrage_fertig_mit_rest: '100 % fertig, aber noch Restaufwand – bitte bestätigen.',
};

/** Was im Formular steht — als Text, wie getippt. */
export interface MeldeEingabe {
  readonly restHours: string;
  readonly fertigstellungsgrad: string;
  readonly bezugswoche: IsoWeek;
  readonly kommentar: string;
}

export function leereEingabe(heute: IsoWeek): MeldeEingabe {
  return { restHours: '', fertigstellungsgrad: '', bezugswoche: heute, kommentar: '' };
}

/** Die waehlbaren Bezugswochen (E6): die laufende und 26 davor, neueste zuerst. */
export function wochenAuswahl(heute: IsoWeek): IsoWeek[] {
  const wochen: IsoWeek[] = [];
  const frueheste = fruehesteBezugswoche(heute);

  for (let woche = heute; compareIsoWeeks(woche, frueheste) >= 0; woche = addWeeks(woche, -1)) {
    wochen.push(woche);
  }

  return wochen;
}

export function wocheAnzeige(woche: IsoWeek): string {
  return `KW ${String(woche.isoWeek)}/${String(woche.isoYear)}`;
}

/** Wert einer Auswahl: `'2026-43'`. */
export function wocheWert(woche: IsoWeek): string {
  return `${String(woche.isoYear)}-${String(woche.isoWeek)}`;
}

export function wocheAusWert(wert: string, wochen: readonly IsoWeek[]): IsoWeek | null {
  return wochen.find((woche) => wocheWert(woche) === wert) ?? null;
}

/** `'12.50'` → `'12,50 h'`. Nur Darstellung, keine Rechnung (Kernregel 10). */
export function stundenAnzeige(stunden: string | null): string {
  return stunden === null ? '—' : `${stunden.replace('.', ',')} h`;
}

const ZEITPUNKT = new Intl.DateTimeFormat('de-DE', {
  timeZone: FACHLICHE_ZEITZONE,
  dateStyle: 'short',
  timeStyle: 'short',
});

export function zeitpunktAnzeige(iso: string): string {
  return ZEITPUNKT.format(new Date(iso));
}

/** Ganzzahl 0–100 aus dem Feld, sonst `null`. Ein Prozentwert ist kein Geld und keine Stunde. */
export function gradAusEingabe(text: string): number | null {
  if (!/^\d{1,3}$/.test(text.trim())) return null;
  const grad = Number(text.trim());
  return grad <= 100 ? grad : null;
}

/** Stunden mit Komma oder Punkt; zum Server immer mit Punkt. */
export function stundenAusEingabe(text: string): string | null {
  const stunden = text.trim().replace(',', '.');
  return REST_STUNDEN_PATTERN.test(stunden) ? stunden : null;
}

export interface MeldeHinweise {
  readonly stunden: string | null;
  readonly grad: number | null;
  /** Wogegen eine Erhoehung verglichen wird (E3): gueltiger Stand der Woche, sonst Plan. */
  readonly vergleich: string | null;
  readonly kommentarPflicht: boolean;
  readonly drift: boolean;
  readonly rueckfrage: boolean;
  readonly kommentarZuLang: boolean;
}

/**
 * Die Hinweise zu einer Eingabe. `historie` steht in Meldereihenfolge, so wie
 * `restaufwand.historie` sie liefert (Voraussetzung von `gueltigeMeldung`).
 */
export function hinweise(
  eingabe: MeldeEingabe,
  historie: readonly RemainingEffortReport[],
  planHours: string | null,
): MeldeHinweise {
  const stunden = stundenAusEingabe(eingabe.restHours);
  const grad = gradAusEingabe(eingabe.fertigstellungsgrad);
  const vorgaenger = gueltigeMeldung(historie, eingabe.bezugswoche);

  return {
    stunden,
    grad,
    vergleich: vorgaenger?.restHours ?? planHours,
    kommentarPflicht:
      stunden !== null && istErhoehung({ restHours: stunden, vorgaenger, planHours }),
    drift: stunden !== null && istDrift({ restHours: stunden, planHoursBeiMeldung: planHours }),
    rueckfrage:
      stunden !== null &&
      grad !== null &&
      verlangtRueckfrage({ restHours: stunden, fertigstellungsgrad: grad }),
    kommentarZuLang: eingabe.kommentar.trim().length > RESTAUFWAND_KOMMENTAR_MAX_LENGTH,
  };
}

/** Die Eingabe an den Server, oder `null`, solange Stunden oder Grad nicht lesbar sind. */
export function zurMeldung(
  id: string,
  workPackageId: string,
  eingabe: MeldeEingabe,
  bestaetigtTrotzFertig: boolean,
): RemainingEffortReportInput | null {
  const stunden = stundenAusEingabe(eingabe.restHours);
  const grad = gradAusEingabe(eingabe.fertigstellungsgrad);

  if (stunden === null || grad === null) return null;

  const kommentar = eingabe.kommentar.trim();

  return {
    id,
    workPackageId,
    restHours: stunden,
    fertigstellungsgrad: grad,
    bezugswoche: eingabe.bezugswoche,
    ...(kommentar === '' ? {} : { kommentar }),
    ...(bestaetigtTrotzFertig ? { bestaetigtTrotzFertig: true } : {}),
  };
}

/**
 * Prueft einen Entwurf aus dem Browserspeicher — veraenderbarer Text, kein
 * Vertrauen. Ob die Werte gueltig sind, sagt der Server.
 */
export function pruefeMeldeEntwurf(roh: unknown): MeldeEingabe | null {
  if (typeof roh !== 'object' || roh === null) return null;

  const { restHours, fertigstellungsgrad, bezugswoche, kommentar } = roh as Record<string, unknown>;
  const woche = bezugswoche as Record<string, unknown> | null | undefined;

  return typeof restHours === 'string' &&
    typeof fertigstellungsgrad === 'string' &&
    typeof kommentar === 'string' &&
    typeof woche === 'object' &&
    woche !== null &&
    Number.isInteger(woche.isoYear) &&
    Number.isInteger(woche.isoWeek)
    ? {
        restHours,
        fertigstellungsgrad,
        kommentar,
        bezugswoche: { isoYear: woche.isoYear as number, isoWeek: woche.isoWeek as number },
      }
    : null;
}

/** Projekte nach Nummer oder Name, Gross- und Kleinschreibung egal. */
export function projekteGefiltert(
  projekte: readonly RestaufwandProjekt[],
  suche: string,
): RestaufwandProjekt[] {
  const text = suche.trim().toLowerCase();
  if (text === '') return [...projekte];

  return projekte.filter(
    (projekt) =>
      projekt.number.toLowerCase().includes(text) || projekt.name.toLowerCase().includes(text),
  );
}
